/**
 * useFeedback
 *
 * Local state + submit logic for the add-feedback screen — the web equivalent
 * of Flutter's `InAppFeedbackScreenState`
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:33-242`).
 *
 * Owns (state + logic only):
 *   - `description` / `setDescription` — the feedback text
 *     (Flutter `_textEditingController`, :36).
 *   - `screenshots` — the local draft screenshot list, max 3
 *     (Flutter `_screenshotAppMediaFiles`, :41).
 *   - `isSubmitting` — the submit-in-flight flag (Flutter `_isSubmitting`, :35).
 *   - `canSubmit` — the submit gate `!isSubmitting && description.length > 0`,
 *     mirroring `_canSubmit` (:39-40). NOTE: parity with Flutter's untrimmed
 *     `text.isNotEmpty` — any non-empty text (even whitespace) enables submit.
 *   - `submitFeedback` — uploads + persists via `SaveFeedbackUseCase`, returns
 *     the saved boolean (Flutter's `saved = await ...saveFeedback(...)`, :227-231).
 *
 * Clean-Architecture note (lessons: "hooks never navigate/toast"): this hook
 * does NOT navigate, open sheets, or fire toasts. The page (Task 10) wires the
 * confirm sheet, the loading overlay, and the unconditional pop around
 * `submitFeedback` — exactly as Flutter's screen owns the confirm sheet +
 * `_submitted`/pop while the repository owns the save (:214-240). Neither client
 * surfaces a success toast; the pop happens regardless of the saved result.
 *
 * Web parity reference: useAddRoadmapItem.ts (sibling add-flow hook that owns
 * draft state + an `isSubmitting`/`canSubmit`-gated submit and leaves the
 * surface to the page).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { SaveFeedbackUseCase } from '@/domain/usecases/feedback/saveFeedbackUseCase';
import { MAX_FEEDBACK_SCREENSHOTS } from '@/domain/constants/feedbackConstants';
import type { FeedbackScreenshotDraft } from '@/domain/input/feedback/saveFeedbackInput';

/**
 * A single locally-picked feedback screenshot — the presentation draft consumed
 * by the picker grid (Task 9) and the page (Task 10).
 *
 * `previewUrl` is an object URL built from `file` for the grid cell's thumbnail;
 * `file` + `aspectRatio` are also handed to `LocalMediaSheet` (which builds its
 * own object URL for the full-size viewer) and to `SaveFeedbackUseCase` on
 * submit. Mirrors Flutter's per-screenshot `AppMedia` (image file +
 * client-computed `aspectRatio`, :73-81).
 */
export interface LocalFeedbackScreenshot {
  /** The local image bytes to upload + preview (Flutter `appMedia.imageFile`). */
  file: File;
  /** Client-computed aspect ratio (width / height), Flutter `appMedia.aspectRatio`. */
  aspectRatio: number;
  /** Object URL built from `file` for the grid cell thumbnail. */
  previewUrl: string;
}

export interface UseFeedbackResult {
  description: string;
  setDescription: (value: string) => void;
  screenshots: LocalFeedbackScreenshot[];
  addScreenshot: (draft: { file: File; aspectRatio: number }) => void;
  removeScreenshot: (index: number) => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  submitFeedback: () => Promise<boolean>;
}

/**
 * @param firebaseUserId - The auth-gated caller uid the page supplies
 *   (`currentUser?.firebaseId`). Mirrors Flutter's `firebaseUser.uid`; a falsy
 *   id short-circuits `submitFeedback` to `false`
 *   (parity with feedback_repository.dart:17-20).
 */
export const useFeedback = (
  firebaseUserId: string | undefined,
): UseFeedbackResult => {
  const [description, setDescription] = useState<string>('');
  const [screenshots, setScreenshots] = useState<LocalFeedbackScreenshot[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Exact Flutter `_canSubmit` predicate (:39-40): not submitting AND non-empty
  // text. Flutter uses `isNotEmpty` (untrimmed), so we mirror with `length > 0`
  // rather than a trimmed check.
  const canSubmit = !isSubmitting && description.length > 0;

  // Track the latest screenshots for the unmount-only cleanup below without
  // making that effect re-run (and prematurely revoke still-visible URLs) on
  // every list change.
  const screenshotsRef = useRef<LocalFeedbackScreenshot[]>(screenshots);
  useEffect(() => {
    screenshotsRef.current = screenshots;
  }, [screenshots]);

  // Revoke any remaining preview object URLs on unmount, avoiding a leak.
  // `removeScreenshot` already revokes the URL it drops, so this only covers
  // screenshots still present when the screen closes.
  useEffect(
    () => () => {
      screenshotsRef.current.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    },
    [],
  );

  const addScreenshot = useCallback(
    (draft: { file: File; aspectRatio: number }) => {
      // Parity with Flutter's `if (_screenshotAppMediaFiles.length >= 3) return;`
      // (:127-129). Guard before creating the object URL so a rejected add never
      // leaks one.
      if (screenshots.length >= MAX_FEEDBACK_SCREENSHOTS) {
        return;
      }
      const previewUrl = URL.createObjectURL(draft.file);
      setScreenshots((prev) => [...prev, { ...draft, previewUrl }]);
    },
    [screenshots.length],
  );

  const removeScreenshot = useCallback(
    (index: number) => {
      // Bounds-check, parity with Flutter's `if (index >= ...length) return;`
      // (:113-116).
      if (index < 0 || index >= screenshots.length) {
        return;
      }
      // Revoke the dropped screenshot's object URL to avoid a leak.
      URL.revokeObjectURL(screenshots[index].previewUrl);
      setScreenshots((prev) => prev.filter((_, i) => i !== index));
    },
    [screenshots],
  );

  const submitFeedback = useCallback(async (): Promise<boolean> => {
    // Guard: mirror Flutter's `if (_isSubmitting || !_canSubmit) return`
    // (:202-204) plus the firebase-user guard (feedback_repository.dart:17-20).
    if (isSubmitting || !canSubmit || !firebaseUserId) {
      return false;
    }

    setIsSubmitting(true);

    const drafts: FeedbackScreenshotDraft[] = screenshots.map((s) => ({
      file: s.file,
      aspectRatio: s.aspectRatio,
    }));

    // The use case → service already catches its own errors and returns `false`
    // (FeedbackService.saveFeedback), mirroring Flutter's internally-caught
    // repository save; parity flow is `_isSubmitting = true` → await save →
    // `_isSubmitting = false` → return saved (:219-231).
    const saved = await new SaveFeedbackUseCase().execute({
      firebaseUserId,
      description,
      screenshots: drafts,
    });

    setIsSubmitting(false);
    return saved;
  }, [isSubmitting, canSubmit, firebaseUserId, description, screenshots]);

  return {
    description,
    setDescription,
    screenshots,
    addScreenshot,
    removeScreenshot,
    isSubmitting,
    canSubmit,
    submitFeedback,
  };
};
